<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel';
import TrackerListLayout from '../../components/TrackerListLayout.vue';
import GroupsPage from './GroupsPage.vue';
import DomainGroupsPage from '../domain-groups/DomainGroupsPage.vue';

const route = useRoute();
const router = useRouter();

const TAB_PATHS = ['/tracker/groups/links', '/tracker/groups/domains'] as const;

function indexFromPath(path: string): number {
  const i = TAB_PATHS.findIndex((p) => path === p || path.startsWith(`${p}/`));
  return i >= 0 ? i : 0;
}

const selectedIndex = ref(indexFromPath(route.path));

watch(
  () => route.path,
  (path) => {
    const next = indexFromPath(path);
    if (next !== selectedIndex.value) selectedIndex.value = next;
  },
);

function onTabChanged(e: { component?: { option: (n: string) => number } }) {
  const idx = e.component?.option('selectedIndex') ?? selectedIndex.value;
  selectedIndex.value = idx;
  const target = TAB_PATHS[idx] ?? TAB_PATHS[0];
  if (route.path !== target) {
    void router.push(target);
  }
}

const pageTitle = computed(() =>
  selectedIndex.value === 1 ? 'Группы · Домены' : 'Группы · Ссылки',
);
</script>

<template>
  <TrackerListLayout :title="pageTitle">
    <template #hint>
      Группы ссылок и доменов в одном месте. Вкладки переключают тип справочника.
    </template>

    <DxTabPanel
      class="groups-tabs"
      height="100%"
      :selected-index="selectedIndex"
      :defer-rendering="false"
      :animation-enabled="true"
      :swipe-enabled="false"
      @selection-changed="onTabChanged"
    >
      <DxItem title="Группы ссылок">
        <div class="groups-tab-body">
          <GroupsPage />
        </div>
      </DxItem>
      <DxItem title="Группы доменов">
        <div class="groups-tab-body">
          <DomainGroupsPage />
        </div>
      </DxItem>
    </DxTabPanel>
  </TrackerListLayout>
</template>

<style scoped>
.groups-tabs {
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.groups-tabs :deep(.dx-tabpanel-container),
.groups-tabs :deep(.dx-multiview-wrapper),
.groups-tabs :deep(.dx-multiview-item-content) {
  height: 100%;
}

.groups-tab-body {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0.5rem 0.1rem 0;
}
</style>
